import gql from 'graphql-tag';
import {useMemo} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {Override} from 'util/Types';

import {AnalysisTemplateSelectorQuery_analysisTemplateListing_AnalysisTemplateNode as AnalysisTemplateSelectorQueryAnalysisTemplateListingAnalysisTemplateNode} from './__generated/AnalysisTemplateSelectorQuery';

export const ANALYSIS_TEMPLATE_SELECTOR_QUERY = gql`
  query AnalysisTemplateSelectorQuery @skipAudit @engComponent(name: ANALYSIS_TOOL) {
    analysisTemplateListing(excludeArchived: true) {
      ... on AnalysisTemplateNode {
        id
        name
        archiveRecord {
          purpose
        }
        configVariables {
          id
          name
          configVariableKey
          variableTypeConfigSpec
        }
        dataFrameVariables {
          id
        }
      }
    }
  }
`;

type AnalysisTemplateConfigVariable = Override<
  AnalysisTemplateSelectorQueryAnalysisTemplateListingAnalysisTemplateNode['configVariables'][number],
  {variableTypeConfigSpec: PipelineConfigJsonSpec}
>;

export type AnalysisTemplateSelectorTemplate = Override<
  AnalysisTemplateSelectorQueryAnalysisTemplateListingAnalysisTemplateNode,
  {configVariables: Array<AnalysisTemplateConfigVariable>}
>;

export interface UseAnalysisTemplatesOptions {
  // This can be used to filter down the templates returned by the query,
  // but it should only be a static filter that doesn't depend on the state of the component using it
  filterTemplates?: (template: AnalysisTemplateSelectorTemplate) => boolean;
}

export interface UseAnalysisTemplatesResult {
  loading: boolean;
  error: boolean;
  allTemplates: Array<AnalysisTemplateSelectorTemplate>;
  validTemplates: Array<AnalysisTemplateSelectorTemplate>;
}

export function useAnalysisTemplatesWithFilter(
  options: UseAnalysisTemplatesOptions = {}
): UseAnalysisTemplatesResult {
  const {filterTemplates} = options;

  const templatesResponse = useBenchlingQuery(ANALYSIS_TEMPLATE_SELECTOR_QUERY);

  const result = useMemo(() => {
    const loading = templatesResponse.type !== QueryResultType.SUCCESS;
    const error = templatesResponse.type === QueryResultType.ERROR;
    const allTemplates: Array<AnalysisTemplateSelectorTemplate> =
      templatesResponse.type === QueryResultType.SUCCESS
        ? (templatesResponse.data as {analysisTemplateListing: Array<AnalysisTemplateSelectorTemplate>})
            .analysisTemplateListing
        : [];

    let validTemplates = allTemplates;

    if (filterTemplates) {
      validTemplates = validTemplates.filter((template: AnalysisTemplateSelectorTemplate) =>
        filterTemplates(template)
      );
    }

    return {
      loading,
      error,
      allTemplates,
      validTemplates,
    };
  }, [templatesResponse, filterTemplates]);

  return result;
}
